import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { api, ApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { fmtDateTime } from "../lib/format";
import { parseWhatsAppText } from "../lib/whatsapp-text";
import { PageHeader } from "../components/PageHeader";
import { EmptyState } from "../components/EmptyState";
import { KpiTile, SparkLine } from "../components/intelligence/KpiTile";
import { WhatsappInstancesPage } from "./WhatsappInstancesPage";
import {
  IconChat, IconSend, IconWhatsApp, IconRefresh, IconAlertTriangle,
  IconUser, IconPlay, IconCog, IconChevronRight, IconInfo,
} from "../components/icons";
import type {
  WhatsAppChannelOverviewDto, WhatsAppThreadDto, WhatsAppMessageDto,
} from "@bi/types";

type Tab = "overview" | "conversations" | "test" | "connection";
type Period = "30" | "90" | "all";

const PERIODS: Array<{ id: Period; label: string }> = [
  { id: "30", label: "30 dias" },
  { id: "90", label: "90 dias" },
  { id: "all", label: "Todo o histórico" },
];

const SUGGESTED_QUESTIONS = [
  "Tem algo preocupante hoje?",
  "Como está meu faturamento este mês?",
  "Quem são os maiores inadimplentes?",
  "Como está o caixa dos próximos 7 dias?",
  "Quais clientes mais faturaram esse mês?",
  "Quais contas tenho que pagar esta semana?",
  "Qual meu resultado estimado esse mês?",
  "Tem algum custo acima do normal?",
];

const ROLE_LABEL: Record<string, string> = {
  admin: "Administrador", gestor: "Gestor", consultor: "Consultor", visualizador: "Visualizador",
};

const daysAgo = (iso: string) => Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
const fmtDate = (iso: string) => new Date(iso).toLocaleDateString("pt-BR");
const pct0 = (v: number) => `${Math.round(v * 100)}%`;

// ─── Texto no formato do WhatsApp (*negrito*, _itálico_, ~riscado~) ───────────
function WaText({ text }: { text: string }) {
  const parts = useMemo(() => parseWhatsAppText(text), [text]);
  return (
    <span className="whitespace-pre-wrap break-words">
      {parts.map((p, i) => p.kind === "bold" ? <strong key={i}>{p.text}</strong>
        : p.kind === "italic" ? <em key={i}>{p.text}</em>
        : p.kind === "strike" ? <s key={i}>{p.text}</s>
        : p.text)}
    </span>
  );
}

function Bubble({ dir, children, meta }: { dir: "inbound" | "outbound"; children: ReactNode; meta?: string }) {
  // inbound = pessoa perguntando (direita, verde do WhatsApp); outbound = SIAG respondendo
  const mine = dir === "inbound";
  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[85%] md:max-w-[72%] rounded-2xl px-3.5 py-2 text-[13px] leading-relaxed shadow-sm ${
        mine ? "bg-[#d9fdd3] text-gray-900 rounded-br-md" : "bg-white text-gray-800 border border-surface-border rounded-bl-md"
      }`}>
        {children}
        {meta && <p className="mt-1 text-[10px] text-gray-400 text-right">{meta}</p>}
      </div>
    </div>
  );
}

// ─── Faixa de conexão ─────────────────────────────────────────────────────────
type WaInstance = { id: string; instanceName: string; status: string };

const CONN: Record<string, { dot: string; label: string; desc: string; cls: string }> = {
  connected:    { dot: "bg-tm-500", label: "Conectado", desc: "respondendo perguntas automaticamente", cls: "text-tm-800" },
  disconnected: { dot: "bg-danger-500", label: "Desconectado", desc: "ninguém recebe resposta até reconectar o número", cls: "text-danger-700" },
  pending:      { dot: "bg-warning-400", label: "Aguardando conexão", desc: "falta ler o QR Code no painel da Evolution", cls: "text-warning-800" },
  error:        { dot: "bg-danger-600", label: "Erro na integração", desc: "verifique a instância em Conexão", cls: "text-danger-700" },
};

function ConnectionStrip({ isAdmin, onManage }: { isAdmin: boolean; onManage: () => void }) {
  const q = useQuery<{ items: WaInstance[] }>({
    queryKey: ["whatsapp-instances"],
    queryFn: () => api.get("/whatsapp/instances"),
    staleTime: 60_000,
    enabled: isAdmin,
    retry: false,
  });

  let dot = "bg-gray-300", title: ReactNode = "Conexão", desc = "", cls = "text-gray-700";
  if (!isAdmin) {
    title = "Status da conexão"; desc = "visível para administradores";
  } else if (q.isLoading) {
    desc = "verificando…";
  } else if (q.error instanceof ApiError && q.error.status === 403) {
    dot = "bg-gray-400"; title = "Envio desligado neste ambiente";
    desc = "demonstração ou ambiente local: nada sai para o WhatsApp. O teste abaixo usa o mesmo motor.";
  } else if (q.error) {
    dot = "bg-danger-500"; title = "Não foi possível ler a conexão"; desc = (q.error as Error).message; cls = "text-danger-700";
  } else {
    const inst = q.data?.items ?? [];
    if (inst.length === 0) {
      dot = "bg-warning-400"; title = "Nenhum número conectado"; desc = "configure a instância em Conexão"; cls = "text-warning-800";
    } else {
      const c = CONN[inst[0]!.status] ?? CONN.pending!;
      dot = c.dot; title = `${c.label} · ${inst[0]!.instanceName}`; desc = c.desc; cls = c.cls;
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-xl border border-surface-border bg-white px-4 py-3">
      <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${dot}`} aria-hidden="true" />
      <IconWhatsApp className="w-4 h-4 text-tm-600" />
      <p className={`text-sm ${cls}`}><strong className="font-semibold">{title}</strong>{desc && <span className="text-gray-500">: {desc}</span>}</p>
      {isAdmin && (
        <div className="ml-auto flex items-center gap-3">
          <button onClick={() => q.refetch()} disabled={q.isFetching} className="text-xs text-gray-500 hover:text-gray-800 inline-flex items-center gap-1">
            <IconRefresh className={`w-3 h-3 ${q.isFetching ? "animate-spin" : ""}`} /> Atualizar
          </button>
          <button onClick={onManage} className="text-xs font-medium text-tm-700 hover:underline">Gerenciar conexão</button>
        </div>
      )}
    </div>
  );
}

// ─── Visão geral ──────────────────────────────────────────────────────────────
function Overview({ onRetry, onOpenThread }: { onRetry: (q: string) => void; onOpenThread: (userId: string) => void }) {
  const [period, setPeriod] = useState<Period>("90");
  const { data, isLoading, error } = useQuery<WhatsAppChannelOverviewDto>({
    queryKey: ["bi", "channel-overview", period],
    queryFn: () => api.get(`/bi/channel/overview?days=${period}`),
    staleTime: 60_000,
  });

  const t = data?.totals;

  // Janela vazia mas com histórico: abre direto no histórico em vez de uma tela de zeros.
  const [autoAll, setAutoAll] = useState(false);
  useEffect(() => {
    if (period === "90" && !autoAll && data && data.totals.questions === 0 && data.lastQuestionAt) {
      setAutoAll(true);
      setPeriod("all");
    }
  }, [period, autoAll, data]);
  const unansweredCount = t ? t.clarification + t.failed : 0;
  const withPhone = data?.people.filter((p) => p.phoneMasked).length ?? 0;
  const active = data?.people.filter((p) => p.questions > 0).length ?? 0;
  const idleDays = data?.lastQuestionAt ? daysAgo(data.lastQuestionAt) : null;
  const maxTopic = Math.max(1, ...(data?.topics.map((x) => x.count) ?? [1]));

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-gray-500 mr-1">Período</span>
        {PERIODS.map((p) => (
          <button key={p.id} onClick={() => setPeriod(p.id)}
            className={`rounded-full px-3 py-1 text-xs font-medium border transition-colors ${
              period === p.id ? "bg-tm-600 border-tm-600 text-white" : "bg-white border-surface-border text-gray-600 hover:border-tm-300"
            }`}>
            {p.label}
          </button>
        ))}
      </div>

      {error && <p className="text-sm text-danger-600">{(error as Error).message}</p>}

      {/* Abandono do canal: é o primeiro sinal a olhar */}
      {idleDays != null && idleDays >= 14 && (
        <div className="flex items-start gap-3 rounded-xl border border-warning-400/40 bg-warning-50 px-4 py-3">
          <IconAlertTriangle className="w-4 h-4 mt-0.5 text-warning-600 flex-shrink-0" />
          <div className="text-sm text-warning-800">
            <p><strong>Ninguém pergunta há {idleDays} dias.</strong> A última pergunta foi em {fmtDate(data!.lastQuestionAt!)}.</p>
            <p className="text-[13px] mt-0.5 text-warning-800/90">
              Vale relembrar a equipe de que o canal existe, com 2 ou 3 perguntas prontas do dia a dia (veja os assuntos abaixo), e conferir se a conexão do número continua ativa.
            </p>
            {autoAll && period === "all" && (
              <p className="mt-1 text-xs text-warning-800/80">Sem perguntas nos últimos 90 dias, por isso a tela mostra todo o histórico.</p>
            )}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiTile loading={isLoading} label="Perguntas"
          value={t ? t.questions.toLocaleString("pt-BR") : "—"}
          visual={data && data.weekly.some((w) => w.count > 0)
            ? <SparkLine values={data.weekly.map((w) => w.count)} label="Perguntas por semana" /> : undefined}
          footnote={t ? `${t.whatsapp} pelo WhatsApp · ${t.web} em testes no BI` : undefined} />
        <KpiTile loading={isLoading} label="Respondidas"
          value={t?.answeredRate != null ? pct0(t.answeredRate) : "—"}
          status={t?.answeredRate != null && t.answeredRate < 0.85 ? "warning" : null}
          footnote={t?.avgDurationMs != null ? `resposta em ${(t.avgDurationMs / 1000).toFixed(1).replace(".", ",")} s, em média` : undefined} />
        <KpiTile loading={isLoading} label="Sem resposta"
          value={t ? unansweredCount.toLocaleString("pt-BR") : "—"}
          tone={unansweredCount > 0 ? "warning" : null}
          footnote={t ? `${t.clarification} pediram esclarecimento · ${t.failed} deram erro` : undefined} />
        <KpiTile loading={isLoading} label="Pessoas usando"
          value={data ? `${active} de ${data.people.length}` : "—"}
          footnote={data ? `${withPhone} com número cadastrado${data.people.length - withPhone > 0 ? `, ${data.people.length - withPhone} sem número` : ""}` : undefined} />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_380px] gap-5">
        {/* Onde o assistente falhou */}
        <section className="card overflow-hidden">
          <header className="px-5 py-3.5 border-b border-surface-border">
            <h2 className="text-sm font-semibold text-gray-900">Onde o assistente não respondeu</h2>
            <p className="text-xs text-gray-500 mt-0.5">Perguntas reais que ficaram sem resposta. Teste de novo para ver se já são entendidas.</p>
          </header>
          {isLoading ? (
            <div className="p-5 space-y-2">{[1, 2, 3].map((i) => <div key={i} className="h-14 rounded-lg bg-gray-100 animate-pulse" />)}</div>
          ) : !data?.unanswered.length ? (
            <EmptyState compact icon={<IconChat className="w-5 h-5" />} title="Nenhuma pergunta sem resposta"
              description={t?.questions ? "Tudo o que foi perguntado no período teve resposta." : "Sem perguntas no período."} />
          ) : (
            <ul className="divide-y divide-surface-border">
              {data.unanswered.map((u) => (
                <li key={u.id} className="px-5 py-3 flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">“{u.question}”</p>
                    {u.reply && <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">SIAG: {u.reply}</p>}
                    <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-gray-400">
                      <span className={`rounded-full px-1.5 py-px font-medium ${u.status === "failed" ? "bg-danger-50 text-danger-600" : "bg-warning-50 text-warning-700"}`}>
                        {u.status === "failed" ? "Erro" : "Pediu esclarecimento"}
                      </span>
                      <span>{u.userName ?? "—"}</span>
                      <span>· {u.channel === "whatsapp" ? "WhatsApp" : "teste no BI"}</span>
                      <span>· {fmtDateTime(u.createdAt)}</span>
                    </p>
                  </div>
                  <button onClick={() => onRetry(u.question)} className="btn-secondary text-xs py-1.5 px-2.5 flex-shrink-0">
                    <IconPlay className="w-3 h-3" /> Testar de novo
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>

        <div className="space-y-5">
          {/* O que perguntam */}
          <section className="card p-5">
            <h2 className="text-sm font-semibold text-gray-900">O que mais perguntam</h2>
            <p className="text-xs text-gray-500 mt-0.5 mb-3">Bom ponto de partida para treinar quem ainda não usa.</p>
            {isLoading ? <div className="h-32 rounded-lg bg-gray-100 animate-pulse" /> : !data?.topics.length ? (
              <p className="text-sm text-gray-400">Sem perguntas no período.</p>
            ) : (
              <ul className="space-y-2">
                {data.topics.map((x) => (
                  <li key={x.label}>
                    <div className="flex items-baseline justify-between gap-2 text-[13px]">
                      <span className="text-gray-700 truncate">{x.label}</span>
                      <span className="font-semibold text-gray-900 tabular-nums">{x.count}</span>
                    </div>
                    <div className="mt-1 h-1.5 rounded-full bg-surface-muted overflow-hidden">
                      <div className="h-full rounded-full bg-tm-500" style={{ width: `${(x.count / maxTopic) * 100}%` }} />
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* Quem pode conversar */}
          <section className="card overflow-hidden">
            <header className="px-5 py-3.5 border-b border-surface-border">
              <h2 className="text-sm font-semibold text-gray-900">Quem pode conversar</h2>
              <p className="text-xs text-gray-500 mt-0.5">O SIAG só responde a números cadastrados no usuário.</p>
            </header>
            {isLoading ? <div className="m-5 h-20 rounded-lg bg-gray-100 animate-pulse" /> : (
              <ul className="divide-y divide-surface-border">
                {data?.people.map((p) => (
                  <li key={p.userId}>
                    <button onClick={() => p.lastAt && onOpenThread(p.userId)} disabled={!p.lastAt}
                      className="w-full px-5 py-2.5 flex items-center gap-3 text-left enabled:hover:bg-surface-muted/60">
                      <span className="w-8 h-8 rounded-full bg-tm-50 text-tm-700 grid place-items-center flex-shrink-0">
                        <IconUser className="w-4 h-4" />
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className="block text-sm font-medium text-gray-900 truncate">{p.name}</span>
                        <span className="block text-[11px] text-gray-500">
                          {ROLE_LABEL[p.role] ?? p.role} · {p.phoneMasked ?? <span className="text-warning-700">sem número cadastrado</span>}
                        </span>
                      </span>
                      <span className="text-right text-[11px] text-gray-500 flex-shrink-0">
                        <span className="block text-sm font-semibold text-gray-900 tabular-nums">{p.questions}</span>
                        {p.lastAt ? `última em ${fmtDate(p.lastAt)}` : "nunca perguntou"}
                      </span>
                      {p.lastAt && <IconChevronRight className="w-3.5 h-3.5 text-gray-300" />}
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <p className="px-5 py-2.5 border-t border-surface-border bg-surface-muted/40 text-[11px] text-gray-500">
              Para liberar alguém, cadastre o WhatsApp no usuário em Configurações → Usuários.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}

// ─── Conversas ────────────────────────────────────────────────────────────────
function Conversations({ initialUserId }: { initialUserId: string | null }) {
  const threads = useQuery<{ items: WhatsAppThreadDto[] }>({
    queryKey: ["bi", "channel-threads"],
    queryFn: () => api.get("/bi/channel/threads"),
    staleTime: 30_000,
  });
  const items = threads.data?.items ?? [];
  const [sel, setSel] = useState<{ userId: string; channel: "whatsapp" | "web" } | null>(null);

  useEffect(() => {
    if (sel || items.length === 0) return;
    const pick = (initialUserId && (items.find((t) => t.userId === initialUserId && t.channel === "whatsapp")
      ?? items.find((t) => t.userId === initialUserId))) || items.find((t) => t.channel === "whatsapp") || items[0]!;
    setSel({ userId: pick.userId, channel: pick.channel });
  }, [items, sel, initialUserId]);

  const msgs = useQuery<{ items: WhatsAppMessageDto[] }>({
    queryKey: ["bi", "channel-thread", sel?.userId, sel?.channel],
    queryFn: () => api.get(`/bi/channel/threads/${sel!.userId}?channel=${sel!.channel}`),
    enabled: !!sel,
    staleTime: 30_000,
  });
  const endRef = useRef<HTMLDivElement>(null);
  useEffect(() => { endRef.current?.scrollIntoView({ block: "end" }); }, [msgs.data]);

  if (threads.isLoading) return <div className="h-96 rounded-xl bg-gray-100 animate-pulse" />;
  if (items.length === 0) {
    return <div className="card"><EmptyState icon={<IconChat className="w-7 h-7" />} title="Nenhuma conversa ainda"
      description="As conversas pelo WhatsApp e os testes feitos aqui aparecem nesta aba." /></div>;
  }

  const current = items.find((t) => t.userId === sel?.userId && t.channel === sel?.channel);
  // agrupa por dia para o separador de data
  let lastDay = "";

  return (
    <div className="card overflow-hidden grid grid-cols-1 md:grid-cols-[300px_minmax(0,1fr)]">
      <nav className="border-b md:border-b-0 md:border-r border-surface-border max-h-[640px] overflow-y-auto" aria-label="Conversas">
        <ul className="divide-y divide-surface-border">
          {items.map((t) => {
            const active = t.userId === sel?.userId && t.channel === sel?.channel;
            return (
              <li key={`${t.userId}-${t.channel}`}>
                <button onClick={() => setSel({ userId: t.userId, channel: t.channel })}
                  className={`w-full text-left px-4 py-3 ${active ? "bg-tm-50" : "hover:bg-surface-muted/60"}`}>
                  <span className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-900 truncate flex-1">{t.name}</span>
                    <span className="text-[10px] text-gray-400 flex-shrink-0">{fmtDate(t.lastAt)}</span>
                  </span>
                  <span className="mt-0.5 flex items-center gap-1.5 text-[11px]">
                    <span className={`rounded px-1 py-px font-medium ${t.channel === "whatsapp" ? "bg-tm-50 text-tm-700" : "bg-info-50 text-info-700"}`}>
                      {t.channel === "whatsapp" ? "WhatsApp" : "Teste no BI"}
                    </span>
                    <span className="text-gray-400">{t.messages} msgs</span>
                  </span>
                  <span className="block mt-1 text-xs text-gray-500 truncate">
                    {t.lastDirection === "outbound" ? "SIAG: " : ""}{t.lastText}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </nav>
      <div className="flex flex-col min-h-[420px] max-h-[640px]">
        {current && (
          <header className="px-5 py-3 border-b border-surface-border flex items-center gap-2">
            <p className="text-sm font-semibold text-gray-900">{current.name}</p>
            <span className="text-xs text-gray-500">· {current.channel === "whatsapp" ? "WhatsApp" : "Teste no BI"} · {current.messages} mensagens</span>
          </header>
        )}
        <div className="flex-1 overflow-y-auto bg-[#efeae2]/60 px-4 py-4 space-y-2">
          {msgs.isLoading ? <div className="h-40 rounded-xl bg-white/60 animate-pulse" /> : (msgs.data?.items ?? []).map((m) => {
            const day = fmtDate(m.createdAt);
            const sep = day !== lastDay ? (lastDay = day) : null;
            return (
              <div key={m.id}>
                {sep && <p className="my-3 text-center"><span className="rounded-md bg-white/90 px-2 py-0.5 text-[11px] text-gray-500 shadow-sm">{sep}</span></p>}
                <Bubble dir={m.direction}
                  meta={new Date(m.createdAt).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}>
                  <WaText text={m.text} />
                </Bubble>
              </div>
            );
          })}
          <div ref={endRef} />
        </div>
      </div>
    </div>
  );
}

// ─── Testar ───────────────────────────────────────────────────────────────────
type ChatTurn = { dir: "inbound" | "outbound"; text: string; at: string };

function TestChat({ seed, onSeedUsed }: { seed: string | null; onSeedUsed: () => void }) {
  const [question, setQuestion] = useState("");
  const [turns, setTurns] = useState<ChatTurn[]>([]);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const endRef = useRef<HTMLDivElement>(null);

  // Mesmo motor do WhatsApp (memória de contexto incluída), sem envio externo.
  const ask = useMutation({
    mutationFn: (q: string) => api.post<{ replies: string[] }>("/bi/converse", { question: q }),
    onSuccess: (data) => {
      const at = new Date().toISOString();
      setTurns((t) => [...t, ...data.replies.map((text) => ({ dir: "outbound" as const, text, at }))]);
    },
  });

  const send = (q: string) => {
    const text = q.trim();
    if (!text || ask.isPending) return;
    setTurns((t) => [...t, { dir: "inbound", text, at: new Date().toISOString() }]);
    setQuestion("");
    ask.mutate(text);
  };

  useEffect(() => {
    if (!seed) return;
    setQuestion(seed);
    onSeedUsed();
    inputRef.current?.focus();
  }, [seed, onSeedUsed]);

  useEffect(() => { endRef.current?.scrollIntoView({ block: "end", behavior: "smooth" }); }, [turns, ask.isPending]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_300px] gap-5">
      <div className="card overflow-hidden flex flex-col h-[600px]">
        <header className="px-5 py-3 border-b border-surface-border flex items-center gap-2">
          <IconWhatsApp className="w-4 h-4 text-tm-600" />
          <p className="text-sm font-semibold text-gray-900">Testar como no WhatsApp</p>
          <span className="text-xs text-gray-500 hidden sm:inline">· mesmo motor e memória da conversa, nada é enviado</span>
          {turns.length > 0 && (
            <button onClick={() => setTurns([])} className="ml-auto text-xs text-gray-500 hover:text-gray-800">Limpar</button>
          )}
        </header>
        <div className="flex-1 overflow-y-auto bg-[#efeae2]/60 px-4 py-4 space-y-2">
          {turns.length === 0 && !ask.isPending && (
            <div className="h-full grid place-items-center text-center px-6">
              <div>
                <IconChat className="w-8 h-8 text-gray-300 mx-auto" />
                <p className="mt-2 text-sm text-gray-600">Pergunte como perguntaria no WhatsApp.</p>
                <p className="text-xs text-gray-400 mt-0.5">Dá para continuar o assunto: “e no mês passado?”, “detalha o primeiro”.</p>
              </div>
            </div>
          )}
          {turns.map((t, i) => (
            <Bubble key={i} dir={t.dir} meta={new Date(t.at).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}>
              <WaText text={t.text} />
            </Bubble>
          ))}
          {ask.isPending && (
            <Bubble dir="outbound"><span className="inline-flex gap-1 py-1" aria-label="SIAG digitando">
              {[0, 1, 2].map((i) => <span key={i} className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: `${i * 120}ms` }} />)}
            </span></Bubble>
          )}
          {ask.isError && (
            <p className="text-center text-xs text-danger-600 inline-flex items-center gap-1 w-full justify-center">
              <IconAlertTriangle className="w-3.5 h-3.5" /> {(ask.error as Error).message}
            </p>
          )}
          <div ref={endRef} />
        </div>
        <div className="border-t border-surface-border p-3 flex items-end gap-2 bg-white">
          <textarea ref={inputRef} value={question} onChange={(e) => setQuestion(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(question); } }}
            placeholder="Ex.: Tem algo preocupante hoje?" rows={1}
            className="flex-1 resize-none rounded-xl border border-surface-border px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-tm-500/20 focus:border-tm-400" />
          <button onClick={() => send(question)} disabled={!question.trim() || ask.isPending} className="btn-primary py-2.5 px-3" aria-label="Enviar">
            <IconSend className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="space-y-4">
        <section className="card p-4">
          <p className="text-sm font-semibold text-gray-900 mb-2.5">Perguntas para testar</p>
          <div className="space-y-1.5">
            {SUGGESTED_QUESTIONS.map((q) => (
              <button key={q} onClick={() => send(q)} disabled={ask.isPending}
                className="w-full text-left text-xs text-gray-700 px-3 py-2 rounded-lg bg-surface-muted border border-surface-border hover:border-tm-200 hover:bg-tm-50 transition-colors disabled:opacity-60">
                {q}
              </button>
            ))}
          </div>
        </section>
        <section className="card p-4 bg-tm-50 border-tm-100">
          <p className="text-xs font-semibold text-tm-800 flex items-center gap-1.5"><IconInfo className="w-3.5 h-3.5" /> Como usar no celular</p>
          <p className="text-xs text-tm-800/90 mt-1.5 leading-relaxed">
            Mande mensagem para o número do SIAG a partir do WhatsApp cadastrado no seu usuário. Dá para mandar áudio também: ele é transcrito e respondido do mesmo jeito.
          </p>
        </section>
      </div>
    </div>
  );
}

// ─── Página ───────────────────────────────────────────────────────────────────
export function WhatsAppPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [tab, setTab] = useState<Tab>("overview");
  const [seed, setSeed] = useState<string | null>(null);
  const [threadUser, setThreadUser] = useState<string | null>(null);

  const TABS: Array<{ id: Tab; label: string; icon: React.ComponentType<{ className?: string }> }> = [
    { id: "overview", label: "Visão geral", icon: IconWhatsApp },
    { id: "conversations", label: "Conversas", icon: IconChat },
    { id: "test", label: "Testar", icon: IconPlay },
    ...(isAdmin ? [{ id: "connection" as Tab, label: "Conexão", icon: IconCog }] : []),
  ];

  return (
    <div className="space-y-5">
      <PageHeader
        title="WhatsApp"
        subtitle="Quem pergunta ao SIAG pelo WhatsApp, sobre o quê, e onde o assistente ainda não responde"
      />

      <ConnectionStrip isAdmin={isAdmin} onManage={() => setTab("connection")} />

      <div className="flex gap-1 border-b border-surface-border overflow-x-auto" role="tablist">
        {TABS.map((t) => (
          <button key={t.id} role="tab" aria-selected={tab === t.id} onClick={() => setTab(t.id)}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px whitespace-nowrap transition-colors ${
              tab === t.id ? "border-tm-600 text-tm-700" : "border-transparent text-gray-500 hover:text-gray-800"
            }`}>
            <t.icon className="w-4 h-4" />{t.label}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <Overview
          onRetry={(q) => { setSeed(q); setTab("test"); }}
          onOpenThread={(id) => { setThreadUser(id); setTab("conversations"); }}
        />
      )}
      {tab === "conversations" && <Conversations key={threadUser ?? "all"} initialUserId={threadUser} />}
      {tab === "test" && <TestChat seed={seed} onSeedUsed={() => setSeed(null)} />}
      {tab === "connection" && isAdmin && <WhatsappInstancesPage />}
    </div>
  );
}
